import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import {
    Users, ChevronRight, ChevronDown, RefreshCw, Download, Loader2, AlertTriangle, Filter, Search, FileText,
} from 'lucide-react';
import { buildVendorDetailsUrl } from '../../config/s3Config';

import {
    SELECT_ALL,
    fetchVVVendorOptions, fetchVVReport, fetchVVVendorDetail,
    clearVVVendorOptions, clearVVReport, resetViewVendor,
    selectVVVendorOptions, selectVVVendorOptionsLoading,
    selectVVReport, selectVVReportLoading, selectVVReportError,
    selectVVDetails, selectVVDetailsLoading, selectVVDetailsError,
} from '../../slices/ReportsSlice/viewVendorSlice';

const inputCls = 'w-full px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed';

// Same option lists as the legacy ViewVendor action.
const VENDOR_TYPES = ['Service Provider', 'Supplier', 'Trading Supply'];
const SUB_TYPES = [
    { value: 'MSME', label: 'MSME' },
    { value: 'NonMSME', label: 'Non-MSME' },
];

const Label = ({ children }) => (
    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">{children}</label>
);

const Field = ({ label, value }) => (
    <div className="min-w-0">
        <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{label}</div>
        <div className="text-xs text-gray-900 dark:text-gray-100 break-words">{(value ?? '').toString().trim() || '-'}</div>
    </div>
);

// Expanded row: the same fields the legacy VendorReportView partial shows.
const VendorDetail = ({ vendor, loading, error }) => {
    if (loading) {
        return <div className="flex items-center gap-2 py-3 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" />Loading vendor details…</div>;
    }
    if (error) return <div className="py-3 text-red-500">{error}</div>;
    if (!vendor) return <div className="py-3 text-gray-400">No details found.</div>;

    const isSP = vendor.Type === 'Service Provider';
    const gst = vendor.VendorGSTData || [];
    const openDocument = () => {
        const url = buildVendorDetailsUrl(vendor.uploaddoc);
        if (!url) { toast.error('No uploaded document available'); return; }
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2">
                <Field label="Type" value={vendor.Type} />
                <Field label="Vendor Name" value={vendor.VendorName} />
                <div className="col-span-2"><Field label="Address" value={vendor.Address} /></div>
                <Field label="Phone No" value={vendor.Phoneno} />
                <Field label="Mobile No" value={vendor.MobileNo} />
                <Field label="Bank Name" value={vendor.BankName} />
                <Field label="Account No" value={vendor.AccountNo} />
                <Field label="IFSC" value={vendor.IFSCode} />
                <Field label="Nature Group" value={vendor.NatureGroupName} />
                <Field label="Master Group" value={vendor.MasterGroup} />
                <Field label="Sub Group" value={vendor.SubGroupId ? vendor.SubGroup : ''} />
                {isSP ? <Field label="PAN No" value={vendor.PanNo} /> : <Field label="CST No" value={vendor.CstNo} />}
                <Field label="GST Applicable" value={vendor.GstApplicable} />
                {isSP && <Field label="PF Reg No" value={vendor.PFRegNo} />}
            </div>
            <div className="flex flex-wrap items-start gap-6">
                {gst.length > 0 && (
                    <table className="border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800">
                        <thead className="bg-indigo-50 dark:bg-indigo-900/20">
                            <tr>
                                <th className="px-3 py-1 text-[10px] font-bold text-gray-600 dark:text-gray-300 uppercase text-left">State</th>
                                <th className="px-3 py-1 text-[10px] font-bold text-gray-600 dark:text-gray-300 uppercase text-left">GST No</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {gst.map((g, i) => (
                                <tr key={i}>
                                    <td className="px-3 py-1 text-[11px]">{g.StateName}</td>
                                    <td className="px-3 py-1 text-[11px] font-mono">{(g.TaxNo || '').trim()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                {vendor.uploaddoc && (
                    <button onClick={openDocument} className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 underline">
                        <FileText className="w-3.5 h-3.5" /> View Uploaded Document
                    </button>
                )}
            </div>
        </div>
    );
};

const ViewVendor = () => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID;

    const vendorOptions = useSelector(selectVVVendorOptions);
    const vendorOptionsLoading = useSelector(selectVVVendorOptionsLoading);
    const report = useSelector(selectVVReport);
    const reportLoading = useSelector(selectVVReportLoading);
    const reportError = useSelector(selectVVReportError);
    const details = useSelector(selectVVDetails);
    const detailsLoading = useSelector(selectVVDetailsLoading);
    const detailsError = useSelector(selectVVDetailsError);

    const [vendorType, setVendorType] = useState('');
    const [subType, setSubType] = useState('');
    const [vendor, setVendor] = useState('');
    const [letter, setLetter] = useState('');
    const [ranParams, setRanParams] = useState(null);
    const [expanded, setExpanded] = useState(null);

    useEffect(() => () => dispatch(resetViewVendor()), [dispatch]);

    const clearResults = () => { setRanParams(null); setExpanded(null); dispatch(clearVVReport()); };

    const handleTypeChange = (val) => {
        setVendorType(val);
        setSubType(''); setVendor(''); setLetter('');
        dispatch(clearVVVendorOptions());
        clearResults();
    };

    const handleSubTypeChange = (val) => {
        setSubType(val);
        setVendor(''); setLetter('');
        dispatch(clearVVVendorOptions());
        clearResults();
        if (val === 'MSME') dispatch(fetchVVVendorOptions({ vendorType, subType: val }));
    };

    // legacy text box: one letter, letters only, shown upper-case
    const handleLetterChange = (val) => {
        setLetter(val.replace(/[^a-zA-Z]/g, '').slice(0, 1).toUpperCase());
        clearResults();
    };

    const handleReset = () => {
        setVendorType(''); setSubType(''); setVendor(''); setLetter('');
        setRanParams(null); setExpanded(null);
        dispatch(resetViewVendor());
    };

    const runReport = () => {
        const missing = [];
        if (!vendorType) missing.push('Type');
        else if (!subType) missing.push('Vendor Type');
        else if (subType === 'MSME' && !vendor) missing.push('Vendor');
        else if (subType === 'NonMSME' && !letter) missing.push('Vendor First Letter');
        if (missing.length) { toast.warning(`Please select: ${missing.join(', ')}`); return; }

        const params = subType === 'MSME'
            ? { Prefix: '', Vendortype: vendorType, Searchbytype: subType, Vendor: vendor }
            : { Prefix: letter, Vendortype: vendorType, Searchbytype: subType, Vendor: '' };
        setRanParams(params);
        setExpanded(null);
        dispatch(fetchVVReport(params));
    };

    const handleRefresh = () => { if (ranParams) { setExpanded(null); dispatch(fetchVVReport(ranParams)); } };

    const toggleRow = (row) => {
        const code = row.VendorCode;
        if (expanded === code) { setExpanded(null); return; }
        setExpanded(code);
        if (!details[code] && !detailsLoading[code]) dispatch(fetchVVVendorDetail({ vendorCode: code, roleId: roleId || 0 }));
    };

    // Same three columns as the legacy Excel export.
    const handleExport = () => {
        if (report.length === 0) { toast.warning('No data to export'); return; }
        const rows = report.map((r) => ({
            VendorCode: r.VendorCode,
            'Vendor Name': (r.VendorName || '').trim(),
            Address: (r.Address || '').trim(),
        }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Vendors');
        XLSX.writeFile(wb, `Vendor_Details_${new Date().toISOString().slice(0, 10)}.xlsx`);
        toast.success('Excel downloaded');
    };

    return (
        <div className="space-y-3 p-4 text-xs">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <div className="p-1.5 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-lg">
                                <Users className="h-4 w-4 text-white" />
                            </div>
                            View Vendor
                        </h1>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Approved vendors by type, MSME or non-MSME, with full vendor details</p>
                        <nav className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-1">
                            <span>Reports</span><ChevronRight className="w-3 h-3" /><span className="text-gray-700 dark:text-gray-200">View Vendor</span>
                        </nav>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={handleRefresh} disabled={reportLoading || !ranParams} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <RefreshCw className={`h-3.5 w-3.5 ${reportLoading ? 'animate-spin' : ''}`} />Refresh
                        </button>
                        <button onClick={handleExport} disabled={report.length === 0} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <Download className="h-3.5 w-3.5" />Export Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-1.5 mb-2">
                    <Filter className="h-3.5 w-3.5 text-indigo-600" />
                    <h2 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Filters</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                    <div>
                        <Label>Type</Label>
                        <select className={inputCls} value={vendorType} onChange={(e) => handleTypeChange(e.target.value)}>
                            <option value="">-Please Select-</option>
                            {VENDOR_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                    {vendorType && (
                        <div>
                            <Label>Vendor Type</Label>
                            <select className={inputCls} value={subType} onChange={(e) => handleSubTypeChange(e.target.value)}>
                                <option value="">-Please Select-</option>
                                {SUB_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>
                    )}
                    {subType === 'MSME' && (
                        <div>
                            <Label>Vendor</Label>
                            <select className={inputCls} value={vendor} onChange={(e) => { setVendor(e.target.value); clearResults(); }} disabled={vendorOptionsLoading}>
                                <option value="">{vendorOptionsLoading ? 'Loading…' : '-Please Select-'}</option>
                                <option value={SELECT_ALL}>Select All</option>
                                {vendorOptions.map((v) => <option key={v.Type} value={v.Type}>{v.Desc}</option>)}
                            </select>
                        </div>
                    )}
                    {subType === 'NonMSME' && (
                        <div>
                            <Label>Vendor Name First Letter</Label>
                            <input className={`${inputCls} uppercase`} value={letter} maxLength={1} placeholder="e.g. A"
                                onChange={(e) => handleLetterChange(e.target.value)} onPaste={(e) => e.preventDefault()}
                                onKeyDown={(e) => { if (e.key === 'Enter') runReport(); }} />
                        </div>
                    )}
                    <div className="flex gap-2">
                        <button onClick={runReport} disabled={reportLoading} className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-60 flex items-center gap-1.5">
                            <Search className="w-3.5 h-3.5" /> View
                        </button>
                        <button onClick={handleReset} className="px-4 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-semibold transition-colors">Reset</button>
                    </div>
                </div>
            </div>

            {/* Result */}
            {reportLoading ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10">
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-600" /><span className="text-xs text-gray-500 dark:text-gray-400">Loading vendors…</span>
                </div>
            ) : reportError ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10 text-red-500">
                    <AlertTriangle className="h-5 w-5" /><span className="text-xs">{reportError}</span>
                </div>
            ) : ranParams && report.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 py-10 text-center text-xs text-gray-400">No Data Found.</div>
            ) : report.length > 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                        <h2 className="text-xs font-bold text-gray-700 dark:text-gray-300">{ranParams?.Vendortype} Vendors ({ranParams?.Searchbytype === 'MSME' ? 'MSME' : 'Non-MSME'})</h2>
                        <span className="text-[11px] text-gray-500 dark:text-gray-400">Vendors: <b className="text-gray-800 dark:text-gray-100">{report.length}</b></span>
                    </div>
                    <div className="overflow-auto max-h-[70vh]">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gradient-to-r from-indigo-600 to-violet-600 sticky top-0 z-10">
                                <tr>
                                    <th className="w-6" />
                                    <th className="px-2 py-1.5 text-[10px] font-bold text-white uppercase text-left whitespace-nowrap">Vendor Code</th>
                                    <th className="px-2 py-1.5 text-[10px] font-bold text-white uppercase text-left">Vendor Name</th>
                                    <th className="px-2 py-1.5 text-[10px] font-bold text-white uppercase text-left">Address</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {report.map((row) => {
                                    const isOpen = expanded === row.VendorCode;
                                    return (
                                        <React.Fragment key={row.VendorId || row.VendorCode}>
                                            <tr onClick={() => toggleRow(row)} className={`cursor-pointer hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-colors ${isOpen ? 'bg-indigo-50 dark:bg-indigo-900/10' : ''}`}>
                                                <td className="px-1 text-center text-indigo-600">
                                                    {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                                </td>
                                                <td className="px-2 py-1 text-xs whitespace-nowrap font-medium">{row.VendorCode}</td>
                                                <td className="px-2 py-1 text-xs">{(row.VendorName || '').trim()}</td>
                                                <td className="px-2 py-1 text-xs text-gray-600 dark:text-gray-300">{(row.Address || '').trim()}</td>
                                            </tr>
                                            {isOpen && (
                                                <tr>
                                                    <td colSpan={4} className="bg-gray-50 dark:bg-gray-900/30 px-6 py-3">
                                                        <VendorDetail vendor={details[row.VendorCode]} loading={detailsLoading[row.VendorCode]} error={detailsError[row.VendorCode]} />
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default ViewVendor;
